import { useState } from "react";
import type { BooleanOverrideKey } from "~/store/useDevOverrides";
import { useDevOverrides } from "~/store/useDevOverrides";

const TOGGLES: {
  key: BooleanOverrideKey;
  label: string;
  description: string;
}[] = [
  {
    key: "simulateSettlement",
    label: "Simulate settlement month",
    description: 'Forces the BudgetWise "Settlement Bill" view on any account.',
  },
];

/**
 * Dev Panel tab of runtime BudgetWise QA toggles. Each writes to the shared
 * `useDevOverrides` store; feature code reads them via `useDevOverride`.
 */
export function BwbTab() {
  const overrides = useDevOverrides();
  // Held as text while typing so intermediate states ("-", "-4.") survive; the
  // store only takes a value once the field is committed.
  const [usageDifferenceDraft, setUsageDifferenceDraft] = useState(
    overrides.usageDifference === null ? "" : String(overrides.usageDifference),
  );

  const commitUsageDifference = () => {
    const raw = usageDifferenceDraft.trim();
    if (raw === "") {
      overrides.setOverride("usageDifference", null);
      return;
    }
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      overrides.setOverride("usageDifference", parsed);
    } else {
      // Unparseable entry — snap back to whatever the store still holds.
      setUsageDifferenceDraft(
        overrides.usageDifference === null
          ? ""
          : String(overrides.usageDifference),
      );
    }
  };

  return (
    <div className="space-y-2 text-xs">
      <p className="text-neutral-500">
        Force BudgetWise UI states for QA. Persists across reloads;
        non-production only.
      </p>
      <div className="divide-y divide-neutral-100">
        {TOGGLES.map(({ key, label, description }) => (
          <label
            key={key}
            className="flex cursor-pointer items-start justify-between gap-3 py-2"
          >
            <span>
              <span className="block font-medium text-neutral-900">
                {label}
              </span>
              <span className="block text-[11px] text-neutral-500">
                {description}
              </span>
            </span>
            <input
              type="checkbox"
              checked={overrides[key]}
              onChange={(event) =>
                overrides.setOverride(key, event.target.checked)
              }
              className="mt-0.5 h-4 w-4 shrink-0 accent-neutral-900"
            />
          </label>
        ))}

        <label className="flex items-start justify-between gap-3 py-2">
          <span>
            <span className="block font-medium text-neutral-900">
              Usage difference
            </span>
            <span className="block text-[11px] text-neutral-500">
              Cumulative energy used vs paid for, in dollars. Positive settles
              into a debit, negative into a credit. Blank uses the real figure.
            </span>
          </span>
          <input
            type="text"
            inputMode="decimal"
            placeholder="real"
            value={usageDifferenceDraft}
            onChange={(event) => setUsageDifferenceDraft(event.target.value)}
            onBlur={commitUsageDifference}
            onKeyDown={(event) => {
              if (event.key === "Enter") commitUsageDifference();
            }}
            className="mt-0.5 w-20 shrink-0 rounded border border-neutral-300 px-1 py-0.5 text-right tabular-nums"
          />
        </label>
      </div>
      <button
        type="button"
        onClick={() => {
          overrides.reset();
          setUsageDifferenceDraft("");
        }}
        className="rounded border border-neutral-300 px-2 py-1 text-neutral-600 hover:bg-neutral-100"
      >
        Reset all
      </button>
    </div>
  );
}
